import { Controller } from "@hotwired/stimulus"

// Analytics controller for tracking page views with Turbo navigation
//
// Handles three navigation types in a Turbo app:
// 1. Initial full page load (connect)
// 2. Turbo Drive visits - full body replacement (turbo:load)
// 3. Turbo Frame navigations that advance the URL (turbo:frame-load)
//
// Usage:
//   Add data-controller="analytics" to the body tag in your layout
//
// Events tracked:
//   - page_view: Fired on initial page load and every navigation that changes the URL
//
// Module-level state that persists across Stimulus reconnections.
// Turbo Drive replaces <body> on each navigation, causing disconnect/connect
// cycles. Instance properties would be lost, but module variables survive.
let lastTrackedUrl = null

// Exported for test teardown only
export function resetLastTrackedUrl() {
  lastTrackedUrl = null
}

export default class extends Controller {
  static values = {
    initialPageTracked: { type: Boolean, default: false }
  }

  connect() {
    if (!this.initialPageTrackedValue) {
      this.trackPageView()
      this.initialPageTrackedValue = true
    }

    document.addEventListener("turbo:load", this.handleTurboLoad)
    document.addEventListener("turbo:frame-load", this.handleFrameLoad)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.handleTurboLoad)
    document.removeEventListener("turbo:frame-load", this.handleFrameLoad)
  }

  // Handle Turbo Drive visits (full page navigations)
  handleTurboLoad = () => {
    if (this.initialPageTrackedValue) {
      this.initialPageTrackedValue = false
      return
    }
    this.trackPageView()
  }

  // Handle Turbo Frame loads that advance the URL
  // (e.g. settings tabs, modals with data-turbo-action="advance")
  handleFrameLoad = () => {
    const currentUrl = window.location.href
    if (lastTrackedUrl === currentUrl) {
      return
    }
    this.trackPageView()
  }

  // Push page_view event to dataLayer for GTM / GA4
  trackPageView() {
    if (typeof window.dataLayer === "undefined") {
      return
    }

    const referrer = lastTrackedUrl || document.referrer || null
    lastTrackedUrl = window.location.href

    window.dataLayer.push({
      event: "page_view",
      page_path: window.location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      page_referrer: referrer
    })
  }

  // Public method to track custom events from other controllers or inline scripts
  //
  // Example:
  //   this.application.getControllerForElementAndIdentifier(document.body, 'analytics')
  //     .trackEvent('button_click', { button_name: 'submit' })
  //
  trackEvent(eventName, eventParams = {}) {
    if (typeof window.dataLayer === "undefined") {
      return
    }

    window.dataLayer.push({
      event: eventName,
      ...eventParams
    })
  }
}
